import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import type { Invoice } from '../types/invoice';
import { sendInvoice } from '../utils/invoiceApi';
import { getCompany } from '../utils/companyApi';
import { toast } from '../utils/toast';

interface InvoiceSendModalProps {
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
  onSent?: (invoice: Invoice) => void;
}

const parseCcInput = (value: string): string | string[] | undefined => {
  const emails = value
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
  if (emails.length === 0) return undefined;
  if (emails.length === 1) return emails[0];
  return emails;
};

const InvoiceSendModal = ({
  invoice,
  isOpen,
  onClose,
  onSent,
}: InvoiceSendModalProps) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setTo(invoice.customer_snapshot.email ?? '');
    setCc('');
    setMessage('');
    setSubject(`Invoice ${invoice.invoice_number}`);
    setLoadingDefaults(true);

    void getCompany()
      .then((company) => {
        if (cancelled) return;
        setSubject(`Invoice ${invoice.invoice_number} from ${company.name}`);
      })
      .catch(() => {
        /* keep number-only subject fallback */
      })
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, invoice]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedTo = to.trim();
    if (!trimmedTo) {
      toast.error('Enter a recipient email address.');
      return;
    }

    try {
      setSubmitting(true);
      const updated = await sendInvoice(invoice.id, {
        to: trimmedTo,
        cc: parseCcInput(cc),
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      toast.success(`Invoice ${updated.invoice_number} sent.`);
      onSent?.(updated);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const busy = submitting || loadingDefaults;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900">
            Send Invoice — {invoice.invoice_number}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="invoice-send-to" className="block text-sm font-medium text-gray-700 mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <input
              id="invoice-send-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
              placeholder="buyer@example.com"
            />
          </div>

          <div>
            <label htmlFor="invoice-send-cc" className="block text-sm font-medium text-gray-700 mb-1">
              CC
            </label>
            <input
              id="invoice-send-cc"
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
              placeholder="ops@example.com, finance@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">Separate multiple addresses with commas.</p>
          </div>

          <div>
            <label
              htmlFor="invoice-send-subject"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Subject
            </label>
            <input
              id="invoice-send-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={busy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label
              htmlFor="invoice-send-message"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Message
            </label>
            <textarea
              id="invoice-send-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={busy}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 disabled:bg-gray-50 resize-y"
              placeholder="Optional note above the default email notice."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex items-center px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Send className="h-5 w-5 mr-2" />
              )}
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceSendModal;
